import React, { useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import {
  GlobalButton,
  GlobalDateField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import { getScheduleColumnDefs } from "../../utils/scheduleColumnDefs";
import {
  formatDateDisplay,
  padTime,
  scheduleDateKey,
  INITIAL_SCHEDULE_DIRECTORY_FILTERS,
} from "../../utils/scheduleDirectoryFilters";
import { scheduleIdText } from "../../utils/scheduleHelpers";
import "../../../call-logs/styles/callLogsDashboard.css";

function getLocalDateString() {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffset).toISOString().split("T")[0];
}

function getActiveColumnFilterValue(column, filters) {
  if (!column) return "";
  switch (column) {
    case "schedule_id":
      return String(filters.schedule_id ?? "");
    case "schedule_date":
      return String(filters.exact_schedule_date ?? "");
    case "schedule_time":
      return padTime(filters.schedule_time);
    case "schedule_title":
      return String(filters.schedule_title ?? "");
    case "schedule_type_name":
      return String(filters.schedule_type_name ?? "");
    case "assigned_to_name":
      return String(filters.assigned_to_name ?? "");
    case "priority_name":
      return String(filters.priority_name ?? "");
    case "status":
      return String(filters.status ?? "");
    case "created_by_name":
      return String(filters.created_by_name ?? "");
    default:
      return "";
  }
}

function uniqSortedOptions(opts) {
  const seen = new Set();
  const out = [];
  for (const o of opts) {
    const k = String(o.value);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ value: k, label: o.label });
  }
  out.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }),
  );
  return out;
}

export default function ScheduleDirectoryFilters({
  isAdmin = false,
  filters,
  onFilterChange,
  rows = [],
  catalogRows = [],
  onColumnVisibilityChange,
  onExportCsv,
}) {
  const columnDefs = getScheduleColumnDefs(isAdmin);
  const PICKER_DEFS = columnDefs.filter((c) => c.id !== "actions");
  const INITIAL_VISIBILITY = Object.fromEntries(
    PICKER_DEFS.map((c) => [c.id, !!c.defaultVisible]),
  );
  const TABLE_FILTER_COLUMNS = PICKER_DEFS.map((c) => ({ value: c.id, label: c.label }));

  const [column, setColumn] = useState("");
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(INITIAL_VISIBILITY);
  const pickerRef = useRef(null);

  const sourceRows = useMemo(() => {
    const map = new Map();
    for (const row of catalogRows || []) {
      if (row?.id != null) map.set(row.id, row);
    }
    for (const row of rows || []) {
      if (row?.id != null) map.set(row.id, row);
    }
    return map.size > 0 ? Array.from(map.values()) : [...(catalogRows || []), ...(rows || [])];
  }, [catalogRows, rows]);

  useEffect(() => {
    if (typeof onColumnVisibilityChange === "function") {
      onColumnVisibilityChange(columnVisibility);
    }
  }, [columnVisibility, onColumnVisibilityChange]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [columnPickerOpen]);

  const handleDateChange = (name) => (nextValue) => {
    onFilterChange((prev) => ({ ...prev, [name]: nextValue || "", page: 1 }));
  };

  const applyColumnValueFilter = (nextColumn, nextValue) => {
    const raw = nextValue != null ? String(nextValue).trim() : "";
    const cleared = { ...INITIAL_SCHEDULE_DIRECTORY_FILTERS };
    onFilterChange((prev) => ({
      ...prev,
      ...cleared,
      start_date: prev.start_date,
      end_date: prev.end_date,
      schedule_id: nextColumn === "schedule_id" ? raw : "",
      exact_schedule_date: nextColumn === "schedule_date" ? raw : "",
      schedule_time: nextColumn === "schedule_time" ? (raw ? padTime(raw) : "") : "",
      schedule_title: nextColumn === "schedule_title" ? raw : "",
      schedule_type_name: nextColumn === "schedule_type_name" ? raw : "",
      assigned_to_name: nextColumn === "assigned_to_name" ? raw : "",
      priority_name: nextColumn === "priority_name" ? raw : "",
      status: nextColumn === "status" ? raw : "",
      created_by_name: nextColumn === "created_by_name" ? raw : "",
      page: 1,
    }));
  };

  const activeValue = getActiveColumnFilterValue(column, filters);

  const valueOptions = useMemo(() => {
    if (!column) return [];
    let options = [];

    if (column === "status") {
      options = [
        { value: "Pending", label: "Pending" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Completed", label: "Completed" },
        { value: "Cancelled", label: "Cancelled" },
      ];
    } else if (column === "schedule_date") {
      const unique = [...new Set(sourceRows.map((x) => scheduleDateKey(x)).filter(Boolean))];
      options = unique.map((key) => ({ value: key, label: formatDateDisplay(key) }));
      options.sort((a, b) => String(b.value).localeCompare(String(a.value)));
    } else if (column === "schedule_time") {
      const unique = [
        ...new Set(sourceRows.map((x) => padTime(x?.schedule_time)).filter(Boolean)),
      ];
      options = unique.map((t) => ({ value: t, label: t }));
    } else if (column === "schedule_id") {
      const unique = [
        ...new Set(sourceRows.map((x) => scheduleIdText(x)).filter(Boolean)),
      ];
      options = unique.map((text) => ({ value: String(text), label: String(text) }));
    } else {
      const unique = [...new Set(sourceRows.map((x) => x?.[column]).filter(Boolean))];
      options = unique.map((text) => ({ value: String(text), label: String(text) }));
    }

    if (activeValue && !options.some((o) => String(o.value) === String(activeValue))) {
      let injectLabel = String(activeValue);
      if (column === "schedule_date") injectLabel = formatDateDisplay(activeValue);
      options = [{ value: String(activeValue), label: injectLabel }, ...options];
    }

    return column === "status" ? options : uniqSortedOptions(options);
  }, [column, sourceRows, activeValue]);

  const handleColumnChange = (nextColumn) => {
    const normalized = nextColumn || "";
    setColumn(normalized);
    applyColumnValueFilter(normalized, "");
  };

  const handleValueChange = (nextValue) => {
    applyColumnValueFilter(column, nextValue != null ? String(nextValue) : "");
  };

  const handleReset = () => {
    setColumn("");
    onFilterChange(() => ({ ...INITIAL_SCHEDULE_DIRECTORY_FILTERS }));
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.75rem",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalDateField
            label="From Date"
            value={filters.start_date || ""}
            onChange={handleDateChange("start_date")}
            max={getLocalDateString()}
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalDateField
            label="To Date"
            value={filters.end_date || ""}
            onChange={handleDateChange("end_date")}
            max={getLocalDateString()}
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalSelectField
            label="Column"
            value={column}
            onChange={handleColumnChange}
            options={TABLE_FILTER_COLUMNS}
            placeholderOption="Select"
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalSelectField
            label="Value"
            value={activeValue}
            onChange={handleValueChange}
            options={valueOptions}
            placeholderOption="All"
            disabled={!column}
          />
        </div>
        <div style={{ flex: "0 0 auto" }}>
          <GlobalButton type="button" size="sm" variant="ghost" onClick={handleReset}>
            Clear filter
          </GlobalButton>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.65rem",
          marginTop: "0.65rem",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
          <div className="vt-column-picker" ref={pickerRef}>
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setColumnPickerOpen((open) => !open)}
              style={{ width: "fit-content", whiteSpace: "nowrap" }}
            >
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {columnPickerOpen ? (
              <div
                className="vt-column-picker__panel vt-column-picker__panel--call-logs"
                role="group"
                aria-label="Choose visible columns"
              >
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {PICKER_DEFS.map((c) => {
                    const enabled = columnVisibility[c.id] !== false;
                    return (
                      <li key={c.id}>
                        <label className="vt-column-picker__row">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() =>
                              setColumnVisibility((prev) => ({
                                ...INITIAL_VISIBILITY,
                                ...prev,
                                [c.id]: !enabled,
                              }))
                            }
                          />
                          <span>{c.label}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
        <GlobalButton
          type="button"
          size="sm"
          variant="ghost"
          onClick={onExportCsv}
          style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap" }}
        >
          Export CSV
        </GlobalButton>
      </div>
    </div>
  );
}
